import { z } from 'zod';
import { decodeJSON } from './json';

export const REFERENCE_VERSION = 1;
// JS `$` can stop before a final line terminator; require a whole-string match.
const matches = (pattern: RegExp, value: string) => pattern.exec(value)?.[0] === value;
const pattern = (re: RegExp) => z.string().refine(value => matches(re, value));
const nativeID = pattern(/^[0-9a-f]{32}$/);
const localID = pattern(/^[A-Za-z0-9_-]{1,64}$/);
const hash = pattern(/^[0-9a-f]{64}$/);
const revision = pattern(/^[1-9][0-9]{0,18}$/).refine(s => s.length < 19 || s <= '9223372036854775807');
export { nativeID as nativeIDSchema, revision as positiveRevisionSchema };
const dnsHost = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
function origin(s: string): boolean {
  if (s.length > 2048 || !s.startsWith('https://')) return false;
  const parts = s.slice(8).split(':');
  if (parts.length > 2 || parts[0]!.length > 253 || !matches(dnsHost, parts[0]!)) return false;
  if (parts.length === 2 && (!matches(/^[1-9][0-9]{0,4}$/, parts[1]!) || Number(parts[1]) > 65535 || Number(parts[1]) === 443)) return false;
  return true;
}
function did(s: string): boolean {
  return s.length <= 2048 && (matches(/^did:plc:[a-z2-7]{24}$/, s) || matches(/^did:web:(?:[A-Za-z0-9._-]|%[A-Fa-f0-9]{2})+(?::(?:[A-Za-z0-9._-]|%[A-Fa-f0-9]{2})+)*$/, s));
}
const nsid = /^[a-zA-Z](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+(?:\.[a-zA-Z][a-zA-Z0-9]{0,62})$/;
function atURI(s: string): boolean {
  if (s.length > 8192 || !s.startsWith('at://')) return false;
  const p = s.slice(5).split('/');
  return p.length === 3 && did(p[0]!) && p[1]!.length <= 317 && matches(nsid, p[1]!) && matches(/^[a-zA-Z0-9_~.:-]{1,512}$/, p[2]!) && p[2] !== '.' && p[2] !== '..';
}
function cidDigest(s: string, codec: number): string | undefined {
  if (!matches(/^b[a-z2-7]{58}$/, s)) return undefined;
  const alphabet = 'abcdefghijklmnopqrstuvwxyz234567', bytes: number[] = [];
  let bits = 0, buffer = 0;
  for (const char of s.slice(1)) {
    buffer = (buffer << 5) | alphabet.indexOf(char); bits += 5;
    if (bits >= 8) { bits -= 8; bytes.push((buffer >> bits) & 255); buffer &= (1 << bits) - 1; }
  }
  if (buffer !== 0 || bytes.length !== 36 || bytes[0] !== 1 || bytes[1] !== codec || bytes[2] !== 0x12 || bytes[3] !== 0x20) return undefined;
  return bytes.slice(4).map(b => b.toString(16).padStart(2, '0')).join('');
}

export const authorityRefSchema = z.strictObject({ kind: z.literal('authority'), id: nativeID });
export const principalRefSchema = z.strictObject({ kind: z.literal('principal'), authority: nativeID, id: nativeID });
export const worldRefSchema = z.strictObject({ kind: z.literal('world'), authority: nativeID, id: localID });
export const roomRefSchema = z.strictObject({ kind: z.literal('room'), world: worldRefSchema, id: localID });
export const objectRefSchema = z.strictObject({ kind: z.literal('object'), room: roomRefSchema, id: localID });
export const localAccountRefSchema = z.strictObject({ kind: z.literal('localAccount'), world: worldRefSchema, id: localID });
export const homeOriginSchema = z.string().refine(origin);
export const homeBindingSchema = z.strictObject({ kind: z.literal('homeBinding'), principal: principalRefSchema, home: authorityRefSchema, origin: homeOriginSchema, revision });
export const atIdentityRefSchema = z.strictObject({ kind: z.literal('atIdentity'), did: z.string().refine(did) });
export const assetVersionRefSchema = z.strictObject({ kind: z.literal('assetVersion'), publisher: nativeID, id: localID, binarySHA256: hash, manifestSHA256: hash });
export const atPublicationRefSchema = z.strictObject({ kind: z.literal('atPublication'), uri: z.string().refine(atURI), recordCID: z.string().refine(s => cidDigest(s, 0x71) !== undefined), blobCID: z.string(), binarySHA256: hash })
  .refine(r => cidDigest(r.blobCID, 0x55) === r.binarySHA256);
export const referenceSchema = z.discriminatedUnion('kind', [authorityRefSchema, principalRefSchema, worldRefSchema, roomRefSchema, objectRefSchema, localAccountRefSchema, homeBindingSchema, atIdentityRefSchema, assetVersionRefSchema, atPublicationRefSchema]);
const envelope = z.strictObject({ version: z.literal(REFERENCE_VERSION), reference: referenceSchema });
export type Reference = z.infer<typeof referenceSchema>;
export type PrincipalRef = z.infer<typeof principalRefSchema>;
export type HomeBinding = z.infer<typeof homeBindingSchema>;

export function legacyWorldReference(worldID: string): z.infer<typeof worldRefSchema> {
  return worldRefSchema.parse({ kind: 'world', authority: worldID, id: worldID });
}

// Syntax only. Authentication, issuer trust and rights checks belong to C-AUTH.
export function decodeReference(input: string | Uint8Array): Reference { return envelope.parse(decodeJSON(input)).reference; }
export function encodeReference(reference: Reference): string {
  const text = JSON.stringify({ version: REFERENCE_VERSION, reference });
  decodeReference(text); return text;
}
export function referenceKey(reference: Reference): string {
  const r = decodeReference(encodeReference(reference));
  let parts: string[];
  switch (r.kind) {
    case 'authority': parts = [r.kind, r.id]; break;
    case 'principal': case 'world': parts = [r.kind, r.authority, r.id]; break;
    case 'room': case 'localAccount': parts = [r.kind, r.world.authority, r.world.id, r.id]; break;
    case 'object': parts = [r.kind, r.room.world.authority, r.room.world.id, r.room.id, r.id]; break;
    case 'homeBinding': parts = [r.kind, r.principal.authority, r.principal.id, r.home.id, r.origin, r.revision]; break;
    case 'atIdentity': parts = [r.kind, r.did]; break;
    case 'assetVersion': parts = [r.kind, r.publisher, r.id, r.binarySHA256, r.manifestSHA256]; break;
    case 'atPublication': parts = [r.kind, r.uri, r.recordCID, r.blobCID, r.binarySHA256]; break;
  }
  return `c-ref:1:${JSON.stringify(parts)}`;
}
